import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';

import { Observable, of } from 'rxjs';
import { delay, map, tap } from 'rxjs/operators';

import {
  PRODUCT_UPDATES_CONFIG,
  type ProductUpdatesConfig,
} from '../config/product-updates-config.provider';
import {
  getMockProductUpdates,
  getMockProductUpdateBySlug,
  getMockUpdatesByMainTab,
  getMockUpdatesByProduct,
} from '../data/mock-product-updates';
import type {
  ProductUpdate,
  ProductUpdatesQueryParams,
  ProductUpdatesResponse,
} from '../models/product-update.model';

@Injectable({ providedIn: 'root' })
export class ProductUpdatesHttpService {
  private readonly http = inject(HttpClient);
  private readonly config = inject<ProductUpdatesConfig>(PRODUCT_UPDATES_CONFIG);

  // Set to true to use mock data instead of real API
  private readonly useMockData = false;

  public readonly loading = signal(false);

  public fetchProductUpdates(params: ProductUpdatesQueryParams = {}): Observable<ProductUpdate[]> {
    this.loading.set(true);

    // Use mock data for development
    if (this.useMockData) {
      return of(getMockProductUpdates(params.locale)).pipe(
        delay(800), // Simulate network delay
        tap(() => this.loading.set(false)),
      );
    }

    const httpParams = this.buildHttpParams(params);

    return this.http
      .get<ProductUpdatesResponse>(`${this.config.apiUrl}/product-updates`, {
        params: httpParams,
        headers: { 'x-json-accent': 'pascal' },
      })
      .pipe(
        map((response: ProductUpdatesResponse) => response.data),
        tap(() => this.loading.set(false)),
      );
  }

  public fetchProductUpdatesPaginated(
    params: ProductUpdatesQueryParams = {},
  ): Observable<ProductUpdatesResponse> {
    this.loading.set(true);

    // Use mock data for development
    if (this.useMockData) {
      let filteredUpdates = getMockProductUpdates(params.locale);

      // Filter by update categories (main tab: whats-new, coming-soon, changelog)
      if (params.updateCategories?.length) {
        // Check if this is a main tab filter
        if (params.updateCategories.includes('Coming soon')) {
          filteredUpdates = getMockUpdatesByMainTab('coming-soon');
        } else if (params.updateCategories.includes('Changelog')) {
          filteredUpdates = getMockUpdatesByMainTab('changelog');
        } else {
          // Default to what's new for Feature/Improvement/New
          filteredUpdates = getMockUpdatesByMainTab('whats-new');
        }
      }

      // Filter by product categories (product tab: hire, manage, grow, ai)
      if (params.productCategories?.length) {
        const productCategory = params.productCategories[0];
        const productMap: Record<string, 'hire' | 'manage' | 'grow' | 'ai'> = {
          Hire: 'hire',
          Manage: 'manage',
          Grow: 'grow',
          AI: 'ai',
        };
        const productTab = productMap[productCategory];
        if (productTab) {
          filteredUpdates = getMockUpdatesByProduct(productTab, filteredUpdates);
        }
      }

      // Sort by publishedAt:
      // - Coming soon: ascending (closest date first)
      // - Other tabs: descending (newest first)
      const isComingSoon = params.updateCategories?.includes('Coming soon');
      filteredUpdates = [...filteredUpdates].sort((a, b) => {
        const dateA = new Date(a.publishedAt).getTime();
        const dateB = new Date(b.publishedAt).getTime();
        return isComingSoon ? dateA - dateB : dateB - dateA;
      });

      const page = params.page ?? 1;
      const perPage = params.perPage ?? 10;
      const startIndex = (page - 1) * perPage;
      const paginatedData = filteredUpdates.slice(startIndex, startIndex + perPage);

      return of({
        data: paginatedData,
        meta: {
          total: filteredUpdates.length,
          page,
          perPage,
        },
      }).pipe(
        delay(page === 1 ? 800 : 500), // Faster delay for subsequent pages
        tap(() => this.loading.set(false)),
      );
    }

    const httpParams = this.buildHttpParams(params);

    return this.http
      .get<ProductUpdatesResponse>(`${this.config.apiUrl}/product-updates`, {
        params: httpParams,
        headers: { 'x-json-accent': 'pascal' },
      })
      .pipe(tap(() => this.loading.set(false)));
  }

  public fetchProductUpdateBySlug(slug: string, locale?: string): Observable<ProductUpdate> {
    this.loading.set(true);

    // Use mock data for development
    if (this.useMockData) {
      const update = getMockProductUpdateBySlug(slug);
      if (update) {
        return of(update).pipe(
          delay(300), // Simulate network delay
          tap(() => this.loading.set(false)),
        );
      }
      this.loading.set(false);
      throw new Error(`Article not found: ${slug}`);
    }

    const params = locale ? new HttpParams().set('locale', locale) : new HttpParams();

    return this.http
      .get<{ data: ProductUpdate }>(`${this.config.apiUrl}/product-updates/${slug}`, {
        params,
        headers: { 'x-json-accent': 'pascal' },
      })
      .pipe(
        map((response: { data: ProductUpdate }) => response.data),
        tap(() => this.loading.set(false)),
      );
  }

  private buildHttpParams(params: ProductUpdatesQueryParams): HttpParams {
    let httpParams = new HttpParams();

    if (params.locale) {
      httpParams = httpParams.set('locale', params.locale);
    }

    if (params.productCategories?.length) {
      params.productCategories.forEach((category: string) => {
        httpParams = httpParams.append('productCategories[]', category);
      });
    }

    if (params.updateCategories?.length) {
      params.updateCategories.forEach((category: string) => {
        httpParams = httpParams.append('updateCategories[]', category);
      });
    }

    if (params.page) {
      httpParams = httpParams.set('page', params.page.toString());
    }

    if (params.perPage) {
      httpParams = httpParams.set('pageSize', params.perPage.toString());
    }

    return httpParams;
  }
}
